import {
  customInstanceWithHeader,
  type ResponseWithHeaders,
} from '@fukuri-admins/apis/client/base/axiosInstance';
import {
  getGetApiV1ThreeLetterCodePartnerAdminsContractPlansKey,
  getGetApiV1ThreeLetterCodePartnerAdminsContractPlansUrl,
} from '@fukuri-admins/apis/client/gen/endpoints';
import type {
  GetApiV1ThreeLetterCodePartnerAdminsContractPlans200,
  GetApiV1ThreeLetterCodePartnerAdminsContractPlansParams,
} from '@fukuri-admins/apis/client/gen/model';
import { useFetch } from '@fukuri-admins/hooks/common';
import type { KeyedMutator } from 'swr';

const fetcher = (
  threeLetterCode: string,
  apiParams: GetApiV1ThreeLetterCodePartnerAdminsContractPlansParams
) =>
  customInstanceWithHeader<GetApiV1ThreeLetterCodePartnerAdminsContractPlans200>(
    getGetApiV1ThreeLetterCodePartnerAdminsContractPlansUrl(threeLetterCode, apiParams),
    { method: 'GET' }
  );

type Args = {
  /** パートナーの 3 文字コード */
  threeLetterCode: string;
  /** 契約プラン名（部分一致） */
  name?: string;
  /** ページ番号（1始まりの文字列） */
  page?: string;
};

type ReturnType = {
  /** 契約プラン一覧データ */
  data: GetApiV1ThreeLetterCodePartnerAdminsContractPlans200;
  /** 総ページ数 */
  totalPage: number;
  /** 総件数 */
  totalCount: number;
  /** SWR の mutate 関数 */
  mutate: KeyedMutator<ResponseWithHeaders<GetApiV1ThreeLetterCodePartnerAdminsContractPlans200>>;
};

type IUseGetPartnerAdminsContractPlans = (args: Args) => ReturnType;

/**
 * パートナー管理者向け契約プラン一覧取得フック
 *
 * - ページネーション対応（total-pages / total-count はレスポンスヘッダーから取得）
 * - name パラメータによるプラン名部分一致検索に対応
 * - Suspense を使用してデータを取得する
 * - 廃止済みプランも含めて返却し、フィルタリングは converter 側で行う
 */
export const useGetPartnerAdminsContractPlans: IUseGetPartnerAdminsContractPlans = ({
  threeLetterCode,
  name,
  page,
}) => {
  const apiParams: GetApiV1ThreeLetterCodePartnerAdminsContractPlansParams = {
    ...(name && { name }),
    ...(page && !Number.isNaN(Number(page)) && { page: Number(page) }),
  };

  const { data, mutate } = useFetch<
    ResponseWithHeaders<GetApiV1ThreeLetterCodePartnerAdminsContractPlans200>
  >(
    getGetApiV1ThreeLetterCodePartnerAdminsContractPlansKey(threeLetterCode, apiParams),
    () => fetcher(threeLetterCode, apiParams),
    { suspense: true }
  );

  const totalPage = Number(data.headers['total-pages']) || 0;
  const totalCount = Number(data.headers['total-count']) || 0;

  return {
    data: data.data,
    totalPage,
    totalCount,
    mutate,
  };
};
